import React, { useCallback, useMemo } from 'react';
import { ActionsBar, Button, HalfScreenSection, IconButton_IconSizes } from '@ydistri/ds';
import { Alert, Spin } from 'antd';
import {
  useCreateNewRegionGroupMutation,
  useGetRegionFlowGroupsQuery,
} from '../apiRegionFlowGroups';
import RegionGroup from './RegionGroup';
import { MdAdd } from 'react-icons/md';
import useIsConfigurationEditable from '../../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { reportQuery, useGetValidationProblemsQuery } from '../../../../apis/apiValidationProblems';
import { skipToken } from '@reduxjs/toolkit/query';
import { ValidationArea, ValidationProblemCode } from '@ydistri/api-sdk';

const RegionGroupConfigurations: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: regionGroups, isFetching } = useGetRegionFlowGroupsQuery(
    templateOrCalculation.value,
  );
  const isConfigurationEditable = useIsConfigurationEditable();

  // The banner is the server's D2 problem, not a local count of the groups the editor loaded.
  const { data: regionsReport } = useGetValidationProblemsQuery(
    templateOrCalculation.hasValidValue
      ? reportQuery(
          { type: templateOrCalculation.type, id: templateOrCalculation.id },
          ValidationArea.Regions,
        )
      : skipToken,
  );
  const noGroups = useMemo(
    () => regionsReport?.find(p => p.code === ValidationProblemCode.RegionsNoGroups),
    [regionsReport],
  );

  const [createNewRegionGroup, createNewRegionGroupStatus] = useCreateNewRegionGroupMutation();

  const handleNewRegionGroup = useCallback(() => {
    createNewRegionGroup(templateOrCalculation);
  }, [createNewRegionGroup, templateOrCalculation]);

  const headerActions = useMemo(() => {
    return (
      <ActionsBar moveToRight={true}>
        <Button
          loading={createNewRegionGroupStatus.isLoading}
          $colorVariant="ghost"
          onClick={handleNewRegionGroup}
          data-testid="create_new_region_group_button"
          disabled={!isConfigurationEditable}
        >
          <MdAdd size={IconButton_IconSizes.small} />
          Create region group
        </Button>
      </ActionsBar>
    );
  }, [createNewRegionGroupStatus.isLoading, handleNewRegionGroup, isConfigurationEditable]);

  return (
    <HalfScreenSection header="Region groups" headerActions={headerActions}>
      <Spin spinning={isFetching}>
        {regionGroups?.map(rg => (
          <RegionGroup regionGroup={rg} key={rg.id} />
        ))}
        {noGroups && (
          <Alert
            message="No region groups"
            description={noGroups.message}
            type="warning"
            showIcon
          />
        )}
      </Spin>
    </HalfScreenSection>
  );
};

export default RegionGroupConfigurations;
